import React from "react";
import DateRangeFilter from "./DateRangeFilter";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import {
  formatEuro,
  formatLong,
  formatShortDate,
  periodLabel,
  customerSourceBreakdownRows,
  sourceLabel,
  statusLabel,
  translateServiceLabel,
} from "./formatters";

const scopeNoteKey = (brand) => {
  if (brand === "dr-hoffmann") return "overview.scopeNote.drHoffmann";
  if (brand === "tawan-spa") return "overview.scopeNote.tawanSpa";
  return "overview.scopeNote.all";
};

const StatCard = ({ label, value, meta = [] }) => {
  const rows = meta.filter(Boolean);
  return (
    <div className="team-stat">
      <span className="team-stat-label">{label}</span>
      <span className="team-stat-value">{value}</span>
      {rows.length > 0 && (
        <div className="team-stat-meta">
          {rows.map((item, index) => (
            <span className="team-stat-hint" key={`${label}-${index}`}>
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const OverviewTab = ({
  data,
  loading,
  dateRange,
  onDateRangeChange,
  onOpenCustomers,
}) => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();

  if (loading) return <div className="team-empty">{t("overview.loading")}</div>;
  if (!data) return <div className="team-empty">{t("overview.noData")}</div>;

  const { stats, breakdown, recentSignups, recentBookings, period } = data;
  const hasPeriod = Boolean(period);
  const periodText = periodLabel(t, period);

  return (
    <div className="team-overview">
      <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />

      <p className="team-scope-note">{t(scopeNoteKey(adminBrand))}</p>

      <div className="team-stats-grid">
        <StatCard
          label={t("overview.stats.totalCustomers")}
          value={stats.totalCustomers}
          meta={[
            t("overview.stats.emailVerified", { count: stats.verifiedCustomers }),
            hasPeriod &&
              t("overview.stats.newInPeriod", {
                count: stats.customersInPeriod,
                period: periodText,
              }),
          ]}
        />
        <StatCard
          label={t("overview.stats.qrCustomers")}
          value={stats.qrCustomers ?? stats.tawanQrCustomers}
        />
        <StatCard
          label={t("overview.stats.totalBookings")}
          value={stats.totalBookings}
          meta={[
            t("overview.stats.upcomingToday", {
              upcoming: stats.upcomingBookings,
              today: stats.bookingsToday,
            }),
          ]}
        />
        <StatCard
          label={
            hasPeriod
              ? t("overview.stats.revenueInPeriod")
              : t("overview.stats.revenueTotal")
          }
          value={formatEuro(stats.revenueCents, locale)}
          meta={[
            t("overview.stats.revenueHint"),
            stats.manualRevenueCents > 0 &&
              t("overview.stats.manualRevenuePart", {
                amount: formatEuro(stats.manualRevenueCents, locale),
              }),
            (stats.manualBookingRevenueCents > 0 ||
              stats.manualEntryRevenueCents > 0) &&
              t("overview.stats.manualRevenueBreakdown", {
                bookings: formatEuro(stats.manualBookingRevenueCents || 0, locale),
                entries: formatEuro(stats.manualEntryRevenueCents || 0, locale),
              }),
          ]}
        />
        {adminBrand === "dr-hoffmann" && stats.registrationReminderEmailsSent != null && (
          <div className="team-stat">
            <span className="team-stat-label">{t("overview.stats.registrationReminderSent")}</span>
            <span className="team-stat-value">{stats.registrationReminderEmailsSent}</span>
            <span className="team-stat-hint">
              {t("overview.stats.registrationReminderClicks", {
                clicks: stats.registrationReminderUniqueClicks ?? 0,
                rate:
                  stats.registrationReminderClickRate != null
                    ? `${stats.registrationReminderClickRate}%`
                    : "—",
              })}
            </span>
          </div>
        )}
      </div>

      <div className="team-breakdown">
        <div className="team-breakdown-card">
          <h3>{t("overview.customersBySource")}</h3>
          <ul>
            {customerSourceBreakdownRows(breakdown?.customersBySource).map((row) => (
              <li key={`${row.source}-${row.registrationSite || ""}`}>
                <span>{sourceLabel(t, row.source, row.registrationSite)}</span>
                <strong>{row.count}</strong>
              </li>
            ))}
          </ul>
        </div>
        <div className="team-breakdown-card">
          <h3>{t("overview.bookingsBySource")}</h3>
          <ul>
            {Object.entries(breakdown?.bookingsBySource || {}).map(([key, count]) => (
              <li key={key}>
                <span>{sourceLabel(t, key)}</span>
                <strong>{count}</strong>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="team-activity-grid">
        <section className="team-activity">
          <h3>
            {hasPeriod ? t("overview.recentSignupsPeriod") : t("overview.recentSignups")}
          </h3>
          {recentSignups?.length === 0 ? (
            <p className="team-muted">{t("common.noEntries")}</p>
          ) : (
            <>
              <ul className="team-activity-list">
                {recentSignups.map((u) => (
                  <li key={u._id}>
                    <div>
                      <strong>
                        {u.firstName} {u.lastName}
                      </strong>
                      <span className="team-muted">{u.email}</span>
                    </div>
                    <div className="team-activity-meta">
                      <span className="badge">
                        {sourceLabel(t, u.source, u.registrationSite)}
                      </span>
                      <time>{formatShortDate(u.createdAt, dateLocale)}</time>
                    </div>
                  </li>
                ))}
              </ul>
              {onOpenCustomers && (
                <button
                  type="button"
                  className="team-activity-link"
                  onClick={onOpenCustomers}
                >
                  {t("overview.viewAllSignups")}
                </button>
              )}
            </>
          )}
        </section>

        <section className="team-activity">
          <h3>
            {hasPeriod ? t("overview.recentBookingsPeriod") : t("overview.recentBookings")}
          </h3>
          {recentBookings?.length === 0 ? (
            <p className="team-muted">{t("common.noEntries")}</p>
          ) : (
            <ul className="team-activity-list">
              {recentBookings.map((b) => (
                <li key={b._id}>
                  <div>
                    <strong>{translateServiceLabel(t, b)}</strong>
                    <span className="team-muted">
                      {b.customer?.firstName} {b.customer?.lastName} · {b.customer?.email}
                    </span>
                  </div>
                  <div className="team-activity-meta">
                    <span className="badge">{statusLabel(t, b.status)}</span>
                    <time>{formatLong(b.start, dateLocale)}</time>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default OverviewTab;
